import startCase from "lodash/startCase";
import { Plus, SquarePen, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import CampaignStatusModal from "routes/campaign/components/CampaignStatusModal";
import CreateNoteButton from "components/LogItem/elements/CreateNoteButton";
import EntryDetails from "components/LogItem/elements/EntryDetails";
import { importSuffix } from "components/LogItem/messages/importSuffix";
import { getModelLink } from "components/LogItem/messages/modelLinks";

// Join rows whose creation *is* the "added to campaign" event. The importer still
// emits the older added_to_campaign action, so both render the same sentence.
export const CAMPAIGN_JOIN_TYPES = [
  "CampaignTarget",
  "CampaignCase",
  "CampaignTargetContact",
];

export function campaignMemberType({ contact_id, case_id }) {
  return contact_id ? "Contact" : case_id ? "Case" : "Entity";
}

const memberLink = ({
  contact_id,
  contact_name,
  case_id,
  case_name,
  entity_id,
  entity_name,
}) =>
  getModelLink(
    contact_id || case_id || entity_id,
    campaignMemberType({ contact_id, case_id }),
    { label: contact_name || case_name || entity_name }
  );

const campaignLink = ({ campaign_id, campaign_name }) =>
  getModelLink(campaign_id, "Campaign", { label: campaign_name });

export function renderAddedToCampaignMessage(item) {
  return (
    <p>
      {item.user_name} added {memberLink(item)} to the campaign{" "}
      {campaignLink(item)}
      {importSuffix(item.info?.data_job_id)}.
    </p>
  );
}

const conversionEntry = state => ({
  title: ({ contact_id }) =>
    `${contact_id ? "Contact" : "Entity"} ${state === "converted" ? "Converted" : "Unconverted"}`,
  icon: state === "converted" ? ToggleRight : ToggleLeft,
  message: item => (
    <p>
      {item.user_name} marked {memberLink(item)} as {state}.
    </p>
  ),
});

export const CAMPAIGN_EVENTS = {
  converted: conversionEntry("converted"),
  unconverted: conversionEntry("unconverted"),
  campaign_own_status_changed: {
    title: "Campaign Status Changed",
    icon: SquarePen,
    message: item => (
      <p>
        {item.user_name} changed the status of the campaign {campaignLink(item)}{" "}
        from {startCase(item.info.prev_status)} to{" "}
        {startCase(item.info.new_status)}.
      </p>
    ),
  },
  campaign_status_changed: {
    title: item => `${campaignMemberType(item)} Campaign Status Changed`,
    icon: SquarePen,
    message: item => {
      const {
        user_name,
        info: {
          prev_status,
          new_status,
          prev_effective_date,
          new_effective_date,
          data_job_id,
        },
      } = item;
      const dateChanged = prev_effective_date !== new_effective_date;
      return (
        <>
          <p>
            {prev_status !== new_status ? (
              <>
                {user_name} changed the campaign status of {memberLink(item)}{" "}
                from {prev_status} to {new_status}
              </>
            ) : (
              <>
                {user_name} updated the campaign status of {memberLink(item)}
              </>
            )}
            {importSuffix(data_job_id)}.
          </p>
          <EntryDetails
            rows={[
              {
                label: "Previous Effective Date",
                value: dateChanged ? prev_effective_date : null,
              },
              {
                label: "Effective Date",
                value: dateChanged ? new_effective_date : null,
              },
            ]}
          />
        </>
      );
    },
    actions: ({
      campaign_id,
      entity_id,
      contact_id,
      modelType,
      modelId,
      logId,
      refreshLog,
      item,
    }) => {
      const { new_status, new_effective_date } = item.info;
      return (
        <>
          <CampaignStatusModal
            campaignId={campaign_id}
            campaignStatus={new_status === "(unset)" ? null : new_status}
            entityId={entity_id}
            contactId={contact_id}
            caseId={item.case_id}
            effectiveDate={new_effective_date}
            entityName={item.entity_name}
            trigger={
              <Button variant="outline" size="sm">
                Update Status
              </Button>
            }
            updateData={refreshLog}
          />
          <CreateNoteButton
            modelType={modelType}
            modelId={modelId}
            logId={logId}
          />
        </>
      );
    },
  },
  added_to_campaign: {
    title: item => `${campaignMemberType(item)} added to Campaign`,
    icon: Plus,
    message: renderAddedToCampaignMessage,
  },
  removed_from_campaign: {
    title: item => `${campaignMemberType(item)} removed from Campaign`,
    icon: Trash2,
    message: item => (
      <p>
        {item.user_name} removed {memberLink(item)} from the campaign{" "}
        {campaignLink(item)}
        {importSuffix(item.info.data_job_id)}.
      </p>
    ),
  },
};
